const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const REQUEST_DELAY = 1000; // Rate limit: 1 request per second

// Simple cache to avoid repeated API calls
const cache = new Map();

// Helper to delay requests (rate limiting)
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Reverse geocode: Convert coordinates to human-readable address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} Address information
 */
export const reverseGeocode = async (lat, lng) => {
  const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  
  // Check cache first
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  try {
    await delay(REQUEST_DELAY);
    
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/reverse?` +
      `lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'BookiZap/1.0', // Required by Nominatim
        },
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data = await response.json();
    
    const result = {
      formattedAddress: data.display_name,
      city: data.address.city || data.address.town || data.address.village || '',
      state: data.address.state || '',
      country: data.address.country || '',
      postalCode: data.address.postcode || '',
      locality: data.address.suburb || data.address.neighbourhood || '',
      rawAddress: data.address || {},
      coordinates: {
        latitude: parseFloat(data.lat),
        longitude: parseFloat(data.lon),
      },
    };

    // Cache the result
    cache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    
    throw new Error('Failed to get address from coordinates');
  }
};

/**
 * Search for locations by text query
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of location results
 */
export const searchLocation = async (query) => {
  if (!query || query.trim().length < 3) {
    return [];
  }

  const cacheKey = `search:${query.toLowerCase()}`;
  
  // Check cache first
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  try {
    await delay(REQUEST_DELAY);
    
    const response = await fetch(
      `${NOMINATIM_BASE_URL}/search?` +
      `q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'BookiZap/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Location search failed');
    }

    const data = await response.json();
    
    const results = data.map(item => ({
      formattedAddress: item.display_name,
      city: item.address.city || item.address.town || item.address.village || '',
      state: item.address.state || '',
      country: item.address.country || '',
      postalCode: item.address.postcode || '',
      locality: item.address.suburb || item.address.neighbourhood || '',
      rawAddress: item.address || {},
      coordinates: {
        latitude: parseFloat(item.lat),
        longitude: parseFloat(item.lon),
      },
    }));

    // Cache the results
    cache.set(cacheKey, results);
    
    return results;
  } catch (error) {
    
    throw new Error('Failed to search locations');
  }
};

/**
 * Get a shortened version of the address for display
 * @param {Object} location - Location object
 * @returns {string} Shortened address
 */
export const getShortAddress = (location) => {
  if (!location) return '';
  
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`;
  }
  
  if (location.city) {
    return location.city;
  }
  
  if (location.locality) {
    return location.locality;
  }
  
  return location.formattedAddress?.split(',').slice(0, 2).join(',') || 'Unknown Location';
};
